import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallowStableRecord } from '../../../hooks/useShallowStableRecord';

import { economicScreenRequestFromSite } from '../../../lib/economicContribution/siteScreeningInput';
import {
  createEconomicResearchRun,
  getEconomicResearchRun,
  isEconomicResearchRunActive,
  refreshEconomicResearchRun,
} from '../../../services/economicResearchClient';
import type { SiteEntity } from '../../../types/site';
import type {
  CreateEconomicResearchRunRequest,
  CreateEconomicResearchRunResponse,
  EconomicResearchRunView,
} from '../../../types/economicResearch';

const DEFAULT_POLL_INTERVAL_MS = 2_500;

type CreateRun = (
  request: CreateEconomicResearchRunRequest,
  options?: { signal?: AbortSignal },
) => Promise<CreateEconomicResearchRunResponse>;
type GetRun = (
  runId: string,
  options?: { signal?: AbortSignal },
) => Promise<EconomicResearchRunView>;
type RefreshRun = (
  runId: string,
  options?: { signal?: AbortSignal },
) => Promise<CreateEconomicResearchRunResponse>;

export interface UseEconomicEvidenceResearchOptions {
  site: SiteEntity;
  enabled: boolean;
  pollIntervalMs?: number;
  /** Test seams; production always uses the authenticated research client. */
  createRun?: CreateRun;
  getRun?: GetRun;
  refreshRun?: RefreshRun;
}

export interface EconomicEvidenceResearchController {
  run: EconomicResearchRunView | null;
  eligible: boolean;
  eligibilityMessage: string | null;
  starting: boolean;
  refreshing: boolean;
  transportError: string | null;
  start: () => Promise<void>;
  refresh: () => Promise<void>;
}

function validCoordinates(site: SiteEntity): boolean {
  const { lat, lng } = site.location;
  return Number.isFinite(lat)
    && Number.isFinite(lng)
    && lat >= -90
    && lat <= 90
    && lng >= -180
    && lng <= 180;
}

function eligibility(site: SiteEntity, enabled: boolean): {
  eligible: boolean;
  message: string | null;
  identity: string;
} {
  if (!enabled) {
    return { eligible: false, message: 'Automatic public-record research is not enabled.', identity: `${site.id}:disabled` };
  }
  if (!validCoordinates(site)) {
    return { eligible: false, message: 'Record a valid site location to search official records.', identity: `${site.id}:location-missing` };
  }
  const request = economicScreenRequestFromSite(site);
  const loadIdentity = request == null
    ? 'load-unresolved'
    : request.directJobsLoadRange
      ? `${request.directJobsLoadRange.low}:${request.directJobsLoadRange.high}:${request.directJobsLoadRange.basis}:${request.directJobsLoadRange.phaseBasis}`
      : `${request.directJobs.load.value}:${request.directJobs.load.basis}:${request.directJobs.load.phaseBasis}`;
  const parcelIdentity = (site.siteBoundaries ?? [])
    .map((parcel) => `${parcel.id}:${parcel.parcelId ?? ''}:${parcel.ownership ?? ''}`)
    .sort()
    .join('|');
  return {
    eligible: true,
    message: null,
    identity: [
      site.id,
      site.name.trim(),
      site.address.trim(),
      site.location.lat,
      site.location.lng,
      loadIdentity,
      request?.directJobs.operatingModel ?? site.economicContributionInputs?.operatingModel ?? 'unknown',
      request?.directJobs.workload ?? site.economicContributionInputs?.workload ?? 'unknown',
      parcelIdentity,
    ].join(':'),
  };
}

/** Convert browser transport failures into stable, user-facing recovery copy. */
export function economicResearchTransportErrorMessage(error: unknown): string {
  const message = error instanceof Error
    ? error.message.trim()
    : typeof error === 'string'
      ? error.trim()
      : '';
  if (!message) return 'The public-record service could not be reached. Check the connection and retry.';
  if (/failed to fetch|network\s*error|network request failed|load failed/i.test(message)) {
    return 'The public-record service could not be reached. Check the connection and retry.';
  }
  return message;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';
}

export function useEconomicEvidenceResearch({
  site,
  enabled,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  createRun = createEconomicResearchRun,
  getRun = getEconomicResearchRun,
  refreshRun = refreshEconomicResearchRun,
}: UseEconomicEvidenceResearchOptions): EconomicEvidenceResearchController {
  const eligibilityState = useMemo(() => eligibility(site, enabled), [enabled, site]);
  const [boundRun, setBoundRun] = useState<{
    eligibilityIdentity: string;
    run: EconomicResearchRunView;
  } | null>(null);
  // Bind visible research synchronously to every input used to request it.
  // Effects clear stale state later, but render/export must fail closed in the
  // commit where a same-site location, load, model, workload, or parcel changes.
  const run = boundRun?.eligibilityIdentity === eligibilityState.identity
    ? boundRun.run
    : null;
  const [starting, setStarting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [transportError, setTransportError] = useState<string | null>(null);
  const autoStartedIdentityRef = useRef<string | null>(null);
  const createInFlightRef = useRef<{ identity: string; promise: Promise<void> } | null>(null);
  const activeIdentityRef = useRef<string | null>(null);
  const currentEligibilityIdentityRef = useRef(eligibilityState.identity);
  currentEligibilityIdentityRef.current = eligibilityState.identity;
  const mountedRef = useRef(true);
  const controllersRef = useRef(new Set<AbortController>());

  useEffect(() => {
    // React StrictMode intentionally replays effects; re-arm after its
    // development-only cleanup so the second setup can still receive data.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // StrictMode immediately replays setup after its development-only
      // cleanup. Defer cancellation by one microtask so that replay retains
      // the single idempotent request; a real unmount remains inactive and
      // aborts every outstanding create/poll request.
      queueMicrotask(() => {
        if (mountedRef.current) return;
        for (const controller of controllersRef.current) controller.abort();
        controllersRef.current.clear();
      });
    };
  }, []);

  useEffect(() => {
    // StrictMode replays effects without changing this identity. Do not abort
    // the request started by the first setup merely because the setup itself
    // was replayed.
    if (activeIdentityRef.current === eligibilityState.identity) return;
    activeIdentityRef.current = eligibilityState.identity;
    // A site move, rename, load change, or operating-context change makes every
    // in-flight response belong to the prior canonical identity. Abort those
    // requests before auto-starting the replacement so a slow prior POST or
    // poll can never overwrite the new run in local state.
    for (const controller of controllersRef.current) controller.abort();
    controllersRef.current.clear();
    setBoundRun(null);
    setStarting(false);
    setRefreshing(false);
    setTransportError(null);
    createInFlightRef.current = null;
    // Leaving Findings deliberately clears its local run state. Re-arm the
    // automatic create/get on re-entry so the same canonical site can recover
    // the server's idempotent active or completed run without a manual click.
    if (!eligibilityState.eligible) autoStartedIdentityRef.current = null;
  }, [eligibilityState.eligible, eligibilityState.identity]);

  const start = useCallback(async (): Promise<void> => {
    if (!eligibilityState.eligible) return;
    const requestIdentity = eligibilityState.identity;
    const existing = createInFlightRef.current;
    if (existing?.identity === requestIdentity) return existing.promise;
    const controller = new AbortController();
    controllersRef.current.add(controller);
    setStarting(true);
    setTransportError(null);
    const promise = (async () => {
      try {
        const response = await createRun({
          siteId: site.id,
          ...(site._ownerUid ? { ownerUid: site._ownerUid } : {}),
        }, { signal: controller.signal });
        if (!mountedRef.current
          || controller.signal.aborted
          || currentEligibilityIdentityRef.current !== requestIdentity) return;
        if (response.run.siteId !== site.id) {
          throw new Error('Public-record research returned a run for a different site.');
        }
        setBoundRun({ eligibilityIdentity: requestIdentity, run: response.run });
      } catch (error) {
        if (!controller.signal.aborted
          && !isAbort(error)
          && mountedRef.current
          && currentEligibilityIdentityRef.current === requestIdentity) {
          setTransportError(economicResearchTransportErrorMessage(error));
        }
      } finally {
        controllersRef.current.delete(controller);
        if (mountedRef.current && currentEligibilityIdentityRef.current === requestIdentity) {
          setStarting(false);
        }
        if (createInFlightRef.current?.identity === requestIdentity) {
          createInFlightRef.current = null;
        }
      }
    })();
    createInFlightRef.current = { identity: requestIdentity, promise };
    return promise;
  }, [createRun, eligibilityState.eligible, eligibilityState.identity, site._ownerUid, site.id]);

  useEffect(() => {
    if (site.draftOnly || !eligibilityState.eligible || autoStartedIdentityRef.current === eligibilityState.identity) return;
    autoStartedIdentityRef.current = eligibilityState.identity;
    void start();
  }, [eligibilityState.eligible, eligibilityState.identity, site.draftOnly, start]);

  useEffect(() => {
    if (!isEconomicResearchRunActive(run)) return;
    const pollIdentity = eligibilityState.identity;
    const controller = new AbortController();
    controllersRef.current.add(controller);
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async (): Promise<void> => {
      try {
        const next = await getRun(run!.runId, { signal: controller.signal });
        if (stopped
          || controller.signal.aborted
          || !mountedRef.current
          || currentEligibilityIdentityRef.current !== pollIdentity) return;
        if (next.siteId !== site.id) throw new Error('Public-record research returned a run for a different site.');
        setBoundRun({ eligibilityIdentity: pollIdentity, run: next });
        setTransportError(null);
        if (isEconomicResearchRunActive(next)) timer = setTimeout(() => void poll(), pollIntervalMs);
      } catch (error) {
        if (stopped
          || controller.signal.aborted
          || isAbort(error)
          || !mountedRef.current
          || currentEligibilityIdentityRef.current !== pollIdentity) return;
        setTransportError(economicResearchTransportErrorMessage(error));
        timer = setTimeout(() => void poll(), pollIntervalMs);
      }
    };
    timer = setTimeout(() => void poll(), pollIntervalMs);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      controller.abort();
      controllersRef.current.delete(controller);
    };
  }, [eligibilityState.identity, getRun, pollIntervalMs, run, site.id]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!run || isEconomicResearchRunActive(run) || refreshing) return;
    const refreshIdentity = eligibilityState.identity;
    const controller = new AbortController();
    controllersRef.current.add(controller);
    setRefreshing(true);
    setTransportError(null);
    try {
      const response = await refreshRun(run.runId, { signal: controller.signal });
      if (!mountedRef.current
        || controller.signal.aborted
        || currentEligibilityIdentityRef.current !== refreshIdentity) return;
      if (response.run.siteId !== site.id) throw new Error('Public-record research returned a run for a different site.');
      setBoundRun({ eligibilityIdentity: refreshIdentity, run: response.run });
    } catch (error) {
      if (!controller.signal.aborted
        && !isAbort(error)
        && mountedRef.current
        && currentEligibilityIdentityRef.current === refreshIdentity) {
        setTransportError(economicResearchTransportErrorMessage(error));
      }
    } finally {
      controllersRef.current.delete(controller);
      if (mountedRef.current && currentEligibilityIdentityRef.current === refreshIdentity) {
        setRefreshing(false);
      }
    }
  }, [eligibilityState.identity, refreshRun, refreshing, run, site.id]);

  // Value-stable controller: the host hands this object to a memoised child,
  // so a fresh object per render with unchanged members must not count as a
  // changed prop.
  return useShallowStableRecord({
    run,
    eligible: eligibilityState.eligible,
    eligibilityMessage: eligibilityState.message,
    starting,
    refreshing,
    transportError,
    start,
    refresh,
  });
}
